import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { galleryImageUrl } from "@/lib/gallery";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminGalleryPage() {
    const [token, setToken] = useState(() => localStorage.getItem("gallery-token") || "");
    const [password, setPassword] = useState("");
    const [authError, setAuthError] = useState("");
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(null);
    const fileRef = useRef(null);
    const replaceRefs = useRef({});

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/gallery/images`);
            setImages(res.data || []);
        } catch {
            setImages([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "Galleri — Lej En Pølsevogn.dk";
        let meta = document.querySelector('meta[name="robots"]');
        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "robots");
            document.head.appendChild(meta);
        }
        meta.setAttribute("content", "noindex, nofollow");
        load();
        return () => {
            meta?.setAttribute("content", "");
        };
    }, []);

    const login = async (e) => {
        e.preventDefault();
        setAuthError("");
        setBusy(true);
        try {
            const res = await axios.post(`${API}/gallery/login`, { password });
            localStorage.setItem("gallery-token", res.data.token);
            setToken(res.data.token);
            setPassword("");
        } catch {
            setAuthError("Forkert adgangskode");
        } finally {
            setBusy(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("gallery-token");
        setToken("");
    };

    const upload = async (e) => {
        e.preventDefault();
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("description", description);
            await axios.post(`${API}/gallery/images`, fd, { headers: authHeaders });
            setFile(null);
            setDescription("");
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch {
            alert("Kunne ikke uploade billedet");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Slet dette billede?")) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/gallery/images/${id}`, { headers: authHeaders });
            await load();
        } catch {
            alert("Kunne ikke slette billedet");
        } finally {
            setBusy(false);
        }
    };

    const replace = async (id) => {
        const input = replaceRefs.current[id];
        if (!input || !input.files[0]) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", input.files[0]);
            await axios.put(`${API}/gallery/images/${id}`, fd, { headers: authHeaders });
            input.value = "";
            await load();
        } catch {
            alert("Kunne ikke erstatte billedet");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            <div className="mx-auto max-w-5xl px-5 py-12">
                <header className="mb-10 flex items-center justify-between">
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-poelse">Admin</p>
                        <h1 className="font-serif text-3xl font-bold text-ink">Galleri</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Billederne her vises i hero-karrusellen og "Fra tidligere events".
                        </p>
                    </div>
                    {token && (
                        <button
                            onClick={logout}
                            className="rounded-full border border-ink/15 px-4 py-2 text-sm font-semibold text-ink hover:border-ink/40"
                        >
                            Log ud
                        </button>
                    )}
                </header>

                {!token ? (
                    <form
                        onSubmit={login}
                        className="mx-auto max-w-sm rounded-xl bg-paper p-6 shadow ring-1 ring-border"
                    >
                        <label className="mb-2 block text-sm font-semibold text-ink" htmlFor="gallery-password">
                            Adgangskode
                        </label>
                        <input
                            id="gallery-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mb-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            autoFocus
                        />
                        {authError && <p className="mb-3 text-xs font-semibold text-rust">{authError}</p>}
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full rounded-full bg-poelse py-2.5 text-sm font-bold text-paper hover:bg-poelse-deep disabled:opacity-60"
                        >
                            Log ind
                        </button>
                    </form>
                ) : (
                    <>
                        <form
                            onSubmit={upload}
                            className="mb-10 flex flex-col gap-4 rounded-xl bg-paper p-6 shadow ring-1 ring-border sm:flex-row sm:items-end"
                        >
                            <div className="flex-1">
                                <label className="mb-2 block text-sm font-semibold text-ink" htmlFor="gallery-file">
                                    Tilføj billede
                                </label>
                                <input
                                    id="gallery-file"
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setFile(e.target.files[0] || null)}
                                    className="block w-full text-sm text-muted-foreground"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="mb-2 block text-sm font-semibold text-ink" htmlFor="gallery-desc">
                                    Beskrivelse (valgfri)
                                </label>
                                <input
                                    id="gallery-desc"
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="F.eks. Pølsevogn til bryllup"
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={busy || !file}
                                className="rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-poelse hover:bg-gold-deep disabled:opacity-60"
                            >
                                Upload
                            </button>
                        </form>

                        {loading ? (
                            <p className="text-sm text-muted-foreground">Henter billeder…</p>
                        ) : images.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                Ingen billeder endnu — upload det første heroforneden.
                            </p>
                        ) : (
                            <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                                {images.map((img) => (
                                    <div key={img.id} className="overflow-hidden rounded-xl bg-paper shadow ring-1 ring-border">
                                        <img
                                            src={galleryImageUrl(img)}
                                            alt={img.description || "Billede"}
                                            className="aspect-square w-full object-cover"
                                        />
                                        <div className="space-y-3 p-3">
                                            <p className="truncate text-xs text-muted-foreground" title={img.description}>
                                                {img.description || "Ingen beskrivelse"}
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <label className="flex-1 cursor-pointer text-center text-xs font-semibold text-poelse">
                                                    Erstat
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        ref={(el) => (replaceRefs.current[img.id] = el)}
                                                        onChange={() => replace(img.id)}
                                                    />
                                                </label>
                                                <button
                                                    onClick={() => remove(img.id)}
                                                    className="flex-1 rounded-full border border-rust/40 px-2 py-1 text-xs font-semibold text-rust hover:bg-rust/10"
                                                >
                                                    Slet
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
